import {useState} from "react";

import {UploadOutlined} from "@ant-design/icons";
import {Auth} from "@digatex/digatex-ui-lib";
import {Button, App as AntdApp, Upload} from "antd";

import api from "../../../../api.js";
import {AntdTable} from "@/components/custom/AntdTable/AntdTable.jsx";
import {maxFileSizeMB, properties} from "@/properties.js";
import styles from "./AttachedDocuments.module.css";

export const AttachedDocuments = ({item, documentsList, updateDocsTable}) => {
	const [fileList, setFileList] = useState([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [loading, setLoading] = useState(false);

	const {message} = AntdApp.useApp();

	const props = {
		action: `${properties.apiUrl}/api/documents/save?operator=${item.operator}&material=${item.material}`,
		headers: Auth.headers(),
		showUploadList: false,
		name: fileList.length === 1 && fileList[0].name,
		onChange: handleChange,
		beforeUpload: file => {
			if (getMBValue(file.size) > maxFileSizeMB) {
				message.error(
					`${file.name} is too big! Please do not attach documents greater than ${maxFileSizeMB}MB`,
				);
				return Upload.LIST_IGNORE;
			}
			const allowedTypes = [
				"application/pdf",
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			];
			if (allowedTypes.includes(file.type)) {
				message.info("Start uploading the document");
				return true;
			}
			message.error(`${file.name} has incorrect type! Please attach pdf,xlsx or docx files!`);
			return Upload.LIST_IGNORE;
		},
	};

	const columns = [
		{
			key: "filename",
			title: "File Name",
			dataIndex: "filename",
		},
		{
			key: "user",
			title: "User",
			dataIndex: "user",
		},
		{
			key: "actions",
			title: "Actions",
			dataIndex: "_id",
			render: _id => (
				<div className={styles.beautifulLink} onClick={() => openFile(_id)}>
					Open
				</div>
			),
		},
	];

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
	};

	const hasSelected = selectedRowKeys.length > 0;

	function getMBValue(bytes) {
		const kb = bytes / 1024;
		return kb / 1024;
	}

	function handleChange(info) {
		if (info.file.status === "done") {
			message.success("The file was successfully uploaded");
		}

		if (info.file.status === "error") {
			message.error(info.file.response);
		}

		let newFileList = [info.file];

		newFileList = newFileList.map(file => {
			if (file.response) {
				file.url = file.response.url;
			}
			return file;
		});
		setFileList(newFileList);
		updateDocsTable(item.operator, item.material);
	}

	function openFile(_id) {
		const url = `/document/get`;

		const config = Auth.headers();
		config["responseType"] = "blob";

		api.post(url, {_id}, config)
			.then(response => {
				const file = response.data;
				if (window.navigator && window.navigator.msSaveOrOpenBlob) {
					window.navigator.msSaveOrOpenBlob(file);
				} else {
					const fileURL = URL.createObjectURL(file);
					window.open(fileURL, "_blank");
				}
			})
			.catch(() => {});
	}

	function onSelectChange(newSelectedRowKeys) {
		setSelectedRowKeys(newSelectedRowKeys);
	}

	function _delete() {
		setLoading(true);
		api.post("/documents/delete", selectedRowKeys)
			.then(() => {
				updateDocsTable(item.operator, item.material);
			})
			.catch(error => console.log(error))
			.finally(() => setLoading(false));
	}

	function getDataSource() {
		const dataSource = [];
		for (const doc of documentsList) {
			dataSource.push({...doc, key: doc._id});
		}
		return dataSource;
	}

	return (
		<div data-testid="attached-documents-container">
			<div>
				<div className={styles.uploadButton}>
					<Upload {...props} fileList={fileList}>
						<Button icon={<UploadOutlined />} data-testid="attached-documents-upload-button">
							Click to Attach Documents
						</Button>
					</Upload>
				</div>
			</div>
			<Button type="primary" onClick={_delete} disabled={!hasSelected} loading={loading}>
				Delete
			</Button>
			<AntdTable
				rowSelection={rowSelection}
				tableSize={"small"}
				scroll={300}
				data={getDataSource()}
				columns={columns}
				data-testid="attached-documents-table"
			/>
		</div>
	);
};
